import { queryOptions, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { useCallback } from 'react';

import { fetchWorkspace } from '../api';

import { useWorkspaceId } from '@/shared/hooks/use-workspace-id';
import { createQueryKeys } from '@/shared/query-keys';
import type { Workspace } from '../types';

export function buildWorkspaceQueryOptions(workspaceId: string) {
  const queryKeys = createQueryKeys(workspaceId);

  return queryOptions({
    queryKey: queryKeys.workspace.detail(),
    queryFn: () => fetchWorkspace(workspaceId),
    staleTime: 60_000,
  });
}

export function useWorkspaceSuspenseQuery() {
  const { workspaceId } = useWorkspaceId();

  return useSuspenseQuery(buildWorkspaceQueryOptions(workspaceId));
}

export function useWorkspaceCache() {
  const queryClient = useQueryClient();
  const { workspaceId } = useWorkspaceId();
  const workspaceQueryOptions = buildWorkspaceQueryOptions(workspaceId);

  const invalidate = useCallback(
    () =>
      queryClient.invalidateQueries({
        queryKey: workspaceQueryOptions.queryKey,
      }),
    [queryClient, workspaceQueryOptions.queryKey],
  );

  const refetch = useCallback(
    () => queryClient.fetchQuery(workspaceQueryOptions),
    [queryClient, workspaceQueryOptions],
  );

  const setWorkspace = useCallback(
    (workspace: Workspace) => {
      queryClient.setQueryData(workspaceQueryOptions.queryKey, workspace);
    },
    [queryClient, workspaceQueryOptions.queryKey],
  );

  return {
    invalidate,
    refetch,
    setWorkspace,
  };
}
